'use client';

import { cssVars } from '@/styles/theme';
import { useLocale } from 'next-intl';

interface Tab {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  badge?: number;
}

interface TabsProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  variant?: 'default' | 'pills' | 'underline';
}

export default function Tabs({ tabs, activeTab, onTabChange, variant = 'default' }: TabsProps) {
  const locale = useLocale();
  const isRTL = locale === 'ar';

  if (variant === 'pills') {
    return (
      <div className="flex flex-wrap gap-2">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;
          const Icon = tab.icon;

          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className="relative flex items-center gap-2 rounded-xl px-4 py-2 font-semibold transition-all hover:scale-105"
              style={{
                backgroundColor: isActive ? cssVars.primary.DEFAULT : cssVars.neutral.surface,
                color: isActive ? 'white' : cssVars.neutral.text,
                border: `2px solid ${isActive ? cssVars.primary.DEFAULT : cssVars.neutral.border}`,
              }}
            >
              {Icon && <Icon className="h-5 w-5" />}
              <span>{tab.label}</span>
              {tab.badge !== undefined && tab.badge > 0 && (
                <span
                  className="flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-2 text-xs font-bold"
                  style={{
                    backgroundColor: isActive ? 'white' : cssVars.status.error,
                    color: isActive ? cssVars.primary.DEFAULT : 'white',
                  }}
                >
                  {tab.badge > 99 ? '99+' : tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  if (variant === 'underline') {
    return (
      <div className="flex gap-6 border-b-2" style={{ borderColor: cssVars.neutral.border }}>
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;
          const Icon = tab.icon;

          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className="relative flex items-center gap-2 pb-3 font-semibold transition-all"
              style={{
                color: isActive ? cssVars.primary.DEFAULT : cssVars.neutral.textSecondary,
              }}
            >
              {Icon && <Icon className="h-5 w-5" />}
              <span>{tab.label}</span>
              {tab.badge !== undefined && tab.badge > 0 && (
                <span
                  className="flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-2 text-xs font-bold"
                  style={{
                    backgroundColor: cssVars.status.error,
                    color: 'white',
                  }}
                >
                  {tab.badge > 99 ? '99+' : tab.badge}
                </span>
              )}
              {isActive && (
                <div
                  className="absolute bottom-0 end-0 start-0 h-0.5"
                  style={{ backgroundColor: cssVars.primary.DEFAULT }}
                />
              )}
            </button>
          );
        })}
      </div>
    );
  }

  // Default variant
  return (
    <div className="flex gap-1 rounded-xl p-1" style={{ backgroundColor: cssVars.neutral.bg }}>
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab;
        const Icon = tab.icon;

        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className="relative flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 font-semibold transition-all"
            style={{
              backgroundColor: isActive ? cssVars.neutral.surface : 'transparent',
              color: isActive ? cssVars.secondary.DEFAULT : cssVars.neutral.textSecondary,
            }}
          >
            {Icon && <Icon className="h-5 w-5" />}
            <span>{tab.label}</span>
            {tab.badge !== undefined && tab.badge > 0 && (
              <span
                className="flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-2 text-xs font-bold"
                style={{
                  backgroundColor: cssVars.status.error,
                  color: 'white',
                }}
              >
                {tab.badge > 99 ? '99+' : tab.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
